import { useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  moveZone,
  type ParapheZone,
  type ZoneKind,
} from '../../services/signature/parapheBoxes';
import { PARAPHEUR_COPY } from '../../services/signature/parapheurCopy';

export interface ZoneFill {
  label: string;
  mine: boolean;
  imageUrl?: string | null;
}

interface Props {
  zones: ParapheZone[];
  page: number;
  filledBy: Map<string, ZoneFill>;
  canPlace: boolean;
  canStamp: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleFill: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onChangeKind: (id: string, kind: ZoneKind) => void;
}

export function ParapheZoneMarks({
  zones,
  page,
  filledBy,
  canPlace,
  canStamp,
  selectedId,
  onSelect,
  onDelete,
  onToggleFill,
  onMove,
  onChangeKind,
}: Props) {
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);
  const metaRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    pw: number;
    ph: number;
    moved: boolean;
  } | null>(null);

  return (
    <>
      {zones.filter((z) => z.page === page).map((z) => {
        const shown = drag?.id === z.id ? { ...z, x: drag.x, y: drag.y } : z;
        const fill = filledBy.get(z.id);
        const selected = selectedId === z.id;
        return (
          <div
            key={z.id}
            role="button"
            tabIndex={0}
            className={`absolute border-2 overflow-visible ${
              fill
                ? 'border-cime-primary bg-white/90'
                : 'border-dashed border-cime-primary bg-cime-primary/10'
            } ${selected ? 'ring-2 ring-cime-primary/40' : ''} ${
              canPlace ? 'cursor-grab' : canStamp ? 'cursor-pointer' : 'pointer-events-none'
            }`}
            style={{
              left: `${shown.x * 100}%`,
              top: `${shown.y * 100}%`,
              width: `${shown.w * 100}%`,
              height: `${shown.h * 100}%`,
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => {
              e.stopPropagation();
              onSelect(z.id);
              if (!canPlace) return;
              if ((e.target as HTMLElement).closest('button')) return;
              const pageEl = (e.currentTarget as HTMLElement).offsetParent as HTMLElement | null;
              const r = pageEl?.getBoundingClientRect();
              if (!r || r.width <= 0 || r.height <= 0) return;
              e.currentTarget.setPointerCapture(e.pointerId);
              metaRef.current = {
                id: z.id,
                startX: e.clientX,
                startY: e.clientY,
                origX: z.x,
                origY: z.y,
                pw: r.width,
                ph: r.height,
                moved: false,
              };
            }}
            onPointerMove={(e) => {
              const meta = metaRef.current;
              if (!meta || meta.id !== z.id) return;
              const nx = meta.origX + (e.clientX - meta.startX) / meta.pw;
              const ny = meta.origY + (e.clientY - meta.startY) / meta.ph;
              if (Math.abs(nx - meta.origX) + Math.abs(ny - meta.origY) > 0.008) meta.moved = true;
              const next = moveZone(z, nx, ny);
              setDrag({ id: z.id, x: next.x, y: next.y });
            }}
            onPointerUp={(e) => {
              e.stopPropagation();
              const meta = metaRef.current;
              metaRef.current = null;
              if (meta?.moved) {
                const nx = meta.origX + (e.clientX - meta.startX) / meta.pw;
                const ny = meta.origY + (e.clientY - meta.startY) / meta.ph;
                const next = moveZone(z, nx, ny);
                onMove(z.id, next.x, next.y);
              } else if (canStamp) {
                onToggleFill(z.id);
              }
              setDrag(null);
            }}
          >
            <div className="absolute inset-0 overflow-hidden flex items-center justify-center pointer-events-none">
              {fill?.imageUrl ? (
                <img src={fill.imageUrl} alt={fill.label} className="max-w-full max-h-full object-contain" />
              ) : fill ? (
                <span className="text-[10px] font-semibold text-cime-primary leading-none">{fill.label}</span>
              ) : (
                <span className="text-[10px] text-cime-primary/70 leading-none">
                  {z.kind === 'signature' ? PARAPHEUR_COPY.toolSignature : PARAPHEUR_COPY.toolParaphe}
                </span>
              )}
            </div>
            {canPlace && (
              <button
                type="button"
                className="absolute -top-2.5 -right-2.5 z-10 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center shadow-sm"
                aria-label="Retirer la case"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(z.id);
                }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {canPlace && selected && (
              <div
                className="absolute left-0 top-full mt-1 z-20 flex gap-0.5"
                onPointerDown={(e) => e.stopPropagation()}
              >
                {(['paraphe', 'signature'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      z.kind === kind
                        ? 'bg-cime-primary text-white border-cime-primary'
                        : 'bg-white text-neutral-700 border-neutral-300'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChangeKind(z.id, kind);
                    }}
                  >
                    {kind === 'paraphe' ? PARAPHEUR_COPY.toolParaphe : PARAPHEUR_COPY.toolSignature}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
